import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Box, Button, Divider, IconButton } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  EmployeeCalendarEventLegend,
  EmployeeCalendarLegendConfig,
} from '~/components/EmployeeCalendar';
import { useSxStyles } from '~/components/EmployeeCalendar/EmployeeCalendarLegend/useStyles';
import { mergeSxProps } from '~/utils/helpers/sxProps';

export type EmployeeCalendarLegendProps = {
  legends: EmployeeCalendarEventLegend[];
  toggleLegendEvents?: (key: string | string[]) => void;
  legendsConfig?: EmployeeCalendarLegendConfig;
};

export const EmployeeCalendarLegendItem = (props: {
  legend: EmployeeCalendarEventLegend;
  toggleVisible?: (key: string | string[]) => void;
}) => {
  const {
    legend: { key, content, label, colors, visibilityToggle },
    toggleVisible,
  } = props;

  const sxStyles = useSxStyles();

  return (
    <Box sx={sxStyles.legend}>
      <Box sx={mergeSxProps(sxStyles.legendSample, colors)}>{content}</Box>
      <Box
        sx={mergeSxProps(
          sxStyles.legendLabel,
          visibilityToggle && !!toggleVisible && !visibilityToggle.isVisible
            ? sxStyles.legendLabelHidden
            : undefined,
        )}
      >
        {label}
      </Box>
      {visibilityToggle && !!toggleVisible && (
        <IconButton
          onClick={() => {
            toggleVisible(key);
          }}
          size={'small'}
        >
          {visibilityToggle.isVisible ? (
            <Visibility fontSize={'inherit'} />
          ) : (
            <VisibilityOff fontSize={'inherit'} />
          )}
        </IconButton>
      )}
    </Box>
  );
};

export const EmployeeCalendarLegend = (props: EmployeeCalendarLegendProps) => {
  const { legends, toggleLegendEvents, legendsConfig } = props;

  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const legendRows = useMemo(() => {
    if (!!legendsConfig?.legendRows) {
      return legendsConfig.legendRows.map((keys) =>
        legends.filter((legend) => keys.includes(legend.key)),
      );
    }

    return [legends];
  }, [legendsConfig, legends]);

  const { visibilityTogglesEnabled, visibleLegends, hiddenLegends } =
    useMemo(() => {
      const getLegendKeys = (legends: EmployeeCalendarEventLegend[]) =>
        legends.map((legend) => legend.key);

      const visibilityToggleLegends = legends.filter(
        (legend) => !!legend.visibilityToggle?.isEnabled,
      );

      const visibleLegendKeys = getLegendKeys(
        visibilityToggleLegends.filter(
          (legend) => legend.visibilityToggle?.isVisible,
        ),
      );

      const hiddenLegendKeys = getLegendKeys(
        visibilityToggleLegends.filter(
          (legend) => !visibleLegendKeys.includes(legend.key),
        ),
      );

      const toggleEnabledCount = visibilityToggleLegends.length;

      return {
        visibilityTogglesEnabled:
          toggleEnabledCount > 2 && !!toggleLegendEvents,
        visibleLegends: {
          keys: visibleLegendKeys,
          disabled: visibleLegendKeys.length === toggleEnabledCount,
        },
        hiddenLegends: {
          keys: hiddenLegendKeys,
          disabled: hiddenLegendKeys.length === toggleEnabledCount,
        },
      };
    }, [legends, toggleLegendEvents]);

  const toggleAllLegends = useCallback(
    (legendKeys: string[]) => {
      if (legendKeys.length > 0 && !!toggleLegendEvents) {
        legendKeys.forEach(toggleLegendEvents);
      }
    },
    [toggleLegendEvents],
  );

  return (
    <Box sx={sxStyles.root}>
      {legendRows.map((legends, index, array) => {
        const isLast = array.length === index + 1;

        return (
          <Box key={`legend-row-${index}`} sx={sxStyles.legendRow}>
            {legends.map((legend, index) => {
              return (
                <EmployeeCalendarLegendItem
                  key={`legend-content-${index}`}
                  legend={legend}
                  toggleVisible={toggleLegendEvents}
                />
              );
            })}
            {isLast && visibilityTogglesEnabled && (
              <>
                <Divider
                  orientation={'vertical'}
                  sx={sxStyles.dividerVertical}
                />
                <Button
                  onClick={() => toggleAllLegends(hiddenLegends.keys)}
                  disabled={visibleLegends.disabled}
                  size={'small'}
                >
                  {t('showAll')}
                </Button>
                <Button
                  onClick={() => toggleAllLegends(visibleLegends.keys)}
                  disabled={hiddenLegends.disabled}
                  size={'small'}
                >
                  {t('hideAll')}
                </Button>
              </>
            )}
          </Box>
        );
      })}
    </Box>
  );
};
